import { useState, useEffect } from 'react';
import { Coins, Plus, Clock, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { creditsAPI } from '../lib/api';

export default function CreditBalance({ onBuyCredits, compact = false }) {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      const response = await creditsAPI.getBalance();
      const data = response.data || response;  // Handle both raw data and axios response
      setCredits(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
      setError('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredits();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchCredits, 60000);
    return () => clearInterval(interval);
  }, []);

  // Format time until daily reset
  const formatResetTime = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800/50 rounded-lg animate-pulse">
        <Coins className="w-4 h-4 text-yellow-500" />
        <span className="text-sm text-gray-400">...</span>
      </div>
    );
  }

  if (error || !credits) {
    return null; // Hide if credits system unavailable
  }

  const { balance, daily_free_remaining, daily_free_resets_in, total_available } = credits;

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-1.5 px-2 py-1 h-8 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg"
              onClick={onBuyCredits}
              data-testid="credit-balance-compact"
            >
              <Coins className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium text-white" data-testid="credit-balance-total">{total_available}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-gray-900 border-gray-700">
            <div className="text-sm">
              <p className="font-medium">Credits: {total_available}</p>
              <p className="text-gray-400 text-xs">
                {balance} purchased + {daily_free_remaining} daily free
              </p>
              {daily_free_remaining > 0 && daily_free_resets_in > 0 && (
                <p className="text-gray-500 text-xs mt-1">
                  Daily credits reset in {formatResetTime(daily_free_resets_in)}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-gray-800/80 to-gray-900/80 rounded-xl border border-gray-700/50" data-testid="credit-balance-full">
      {/* Main Balance */}
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-yellow-500/20 rounded-lg">
          <Coins className="w-5 h-5 text-yellow-500" />
        </div>
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-white" data-testid="credit-balance-total">{total_available}</span>
            <span className="text-xs text-gray-400">credits</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span data-testid="credit-balance-purchased">{balance} purchased</span>
            {daily_free_remaining > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1 text-green-400" data-testid="credit-balance-daily-free">
                  <Zap className="w-3 h-3" />
                  {daily_free_remaining} free
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Daily Reset Timer */}
      {daily_free_resets_in > 0 && daily_free_remaining === 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-700/50 rounded text-xs text-gray-400" data-testid="credit-balance-reset-timer">
                <Clock className="w-3 h-3" />
                {formatResetTime(daily_free_resets_in)}
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-gray-900 border-gray-700">
              <p className="text-sm">Free credit resets in {formatResetTime(daily_free_resets_in)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Buy More Button */}
      <Button
        size="sm"
        onClick={onBuyCredits}
        className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-medium"
        data-testid="credit-balance-buy-button"
      >
        <Plus className="w-4 h-4 mr-1" />
        Buy
      </Button>
    </div>
  );
}
